import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Grid, Divider, IconButton, InputAdornment, ButtonGroup } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import axios from "axios";

// Components & Redux Actions
import FilterOrg from "../../OrgAmin/components/FilterOrg";
import { clearSelectedOrg } from "../../../../../features/OrgAdmin/orgAdminSlice";

const initialFormState = {
  email: "",
  password: "",
  confirmPassword: "",
};

function PasswordRReset() {
  const [formData, setFormData] = useState(initialFormState);
  const [loading, setLoading] = useState(false);
  const [resetMode, setResetMode] = useState("org"); // 'org' or 'email'

  // Visibility States
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const dispatch = useDispatch();

  // Snackbar States
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { selectedOrg } = useSelector((state) => state.org);
  const orgEmail = selectedOrg?.orgInfo?.emailId || "";

  // Effect to set email when org is selected (only in 'org' mode)
  useEffect(() => {
    if (resetMode === "org" && orgEmail) {
      setFormData((prev) => ({ ...prev, email: orgEmail }));
    }
  }, [orgEmail, resetMode]);

  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

  const openSuccessSB = () => setSuccessSB(true);
  const closeSuccessSB = () => setSuccessSB(false);
  const openErrorSB = () => setErrorSB(true);
  const closeErrorSB = () => setErrorSB(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleResetPassword = async () => {
    const token = localStorage.getItem("token");

    if (!formData.email || !formData.password || !formData.confirmPassword) {
      setErrorMessage("Please fill all fields.");
      openErrorSB();
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setErrorMessage("Passwords do not match!");
      openErrorSB();
      return;
    }

    setLoading(true);

    try {
      const payload = {
        email: formData.email,
        password: formData.password,
      };

      await axios.post(
        "https://v3.dhundhoo.com/superAdmin/resetUserPassword",
        payload,
        {
          headers: {
            "x-dhundhoo-session": token,
            "Content-Type": "application/json",
          },
        }
      );

      openSuccessSB();
      setFormData(initialFormState);
      setShowPassword(false);
      setShowConfirmPassword(false);
      if(resetMode === 'org') dispatch(clearSelectedOrg());
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "Failed to reset password.");
      openErrorSB();
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFormData(initialFormState);
    if (resetMode === "org") {
      dispatch(clearSelectedOrg());
    }
  };

  return (
    <MDBox py={3} px={2}>
      {/* Selection Buttons */}
      <MDBox mb={4} display="flex" justifyContent="center">
        <ButtonGroup variant="gradient">
          <MDButton 
            color={resetMode === "org" ? "info" : "light"} 
            onClick={() => { setResetMode("org"); handleClear(); }}
          >
            Search by Organization
          </MDButton>
          <MDButton 
            color={resetMode === "email" ? "info" : "light"} 
            onClick={() => { setResetMode("email"); handleClear(); }}
          >
            By Email ID
          </MDButton>
        </ButtonGroup>
      </MDBox>

      {/* Conditional UI */}
      {resetMode === "org" && (
        <MDBox mb={4} display="flex" justifyContent="center">
          <FilterOrg />
        </MDBox>
      )}

      {(resetMode === "email" || (resetMode === "org" && selectedOrg)) ? (
        <MDBox
          sx={{ maxWidth: "600px", margin: "0 auto", p: 3, boxShadow: 3, borderRadius: 2, bgcolor: "background.paper" }}
        >
          <MDTypography variant="h5" mb={3} textAlign="center">
            Reset {resetMode === "org" ? "Org User" : "Direct"} Password
          </MDTypography>

          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            {/* Email Field */}
            <Grid item xs={12}>
              <MDTypography variant="button" fontWeight="medium">User Email:</MDTypography>
              <MDInput
                fullWidth
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter email address"
                disabled={resetMode === "org"} // Org mode mein email auto-fill hoti hai
              />
            </Grid>

            {/* New Password Field */}
            <Grid item xs={12}>
              <MDTypography variant="button" fontWeight="medium">New Password:</MDTypography>
              <MDInput
                fullWidth
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={togglePasswordVisibility} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Confirm Password Field */}
            <Grid item xs={12}>
              <MDTypography variant="button" fontWeight="medium">Confirm Password:</MDTypography>
              <MDInput
                fullWidth
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={toggleConfirmPasswordVisibility} edge="end">
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          <MDBox display="flex" gap={2} mt={4} justifyContent="center">
            <MDButton variant="gradient" color="info" onClick={handleResetPassword} disabled={loading}>
              {loading ? "Resetting..." : "Update Password"}
            </MDButton>
            <MDButton variant="outlined" color="secondary" onClick={handleClear}>
              Clear
            </MDButton>
          </MDBox>
        </MDBox>
      ) : (
        <MDBox display="flex" justifyContent="center" mt={5}>
          <MDTypography variant="h6" color="secondary" opacity={0.7}>
            Please select an organization to proceed.
          </MDTypography>
        </MDBox>
      )}

      {/* Success Snackbar */}
      <MDSnackbar
        color="success"
        icon="check"
        title="Success"
        content="Password reset successfully!"
        open={successSB}
        onClose={closeSuccessSB}
        close={closeSuccessSB}
      />

      {/* Error Snackbar */}
      <MDSnackbar
        color="error"
        icon="warning"
        title="Error"
        content={errorMessage}
        open={errorSB}
        onClose={closeErrorSB}
        close={closeErrorSB}
      />
    </MDBox>
  );
}

export default PasswordRReset;